import { useState, useRef, useCallback, useEffect } from "react";
import { guideInVideoPixels } from "../utils/mrz/mrzImage";

// EN-4743. Glare warning, with the kiosk's thresholds
// (visitly-kiosk-app/ios/Visitly/GlareDetectorFrameProcessorPlugin.mm): a pixel
// counts as glare at luma >= 235; the frame is glare when >= 12% of the central
// 70% of the guide area is; and it's reported only after 3 checks in a row, so a
// fleeting reflection doesn't trigger it.
const GLARE_BRIGHTNESS = 235;
const GLARE_RATIO = 0.12;
const GLARE_ROI = 0.7;
const GLARE_CONFIRM_CHECKS = 3;
const GLARE_INTERVAL_MS = 250;
const SAMPLE_WIDTH = 160;

interface UseGlareCheckOptions {
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  active: boolean;
}

interface UseGlareCheckReturn {
  isGlare: boolean;
  // Current value for code that runs outside a render (the countdown).
  isGlareNow: () => boolean;
}

export const useGlareCheck = ({ videoEl, stageEl, guideEl, active }: UseGlareCheckOptions): UseGlareCheckReturn => {
  const [isGlare, setIsGlare] = useState<boolean>(false);
  const glareRef = useRef<boolean>(false);
  const streakRef = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!active || !videoEl || !stageEl || !guideEl) return;
    if (!canvasRef.current) canvasRef.current = document.createElement("canvas");
    const canvas = canvasRef.current;

    const check = () => {
      const g = guideInVideoPixels(videoEl, stageEl, guideEl);
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!g || !ctx) return;
      const rw = g.w * GLARE_ROI;
      const rh = g.h * GLARE_ROI;
      canvas.width = SAMPLE_WIDTH;
      canvas.height = Math.max(1, Math.round((SAMPLE_WIDTH * rh) / rw));
      ctx.drawImage(videoEl, g.x + (g.w - rw) / 2, g.y + (g.h - rh) / 2, rw, rh, 0, 0, canvas.width, canvas.height);
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let bright = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2] >= GLARE_BRIGHTNESS) bright += 1;
      }
      streakRef.current = bright / (d.length / 4) >= GLARE_RATIO ? streakRef.current + 1 : 0;
      const confirmed = streakRef.current >= GLARE_CONFIRM_CHECKS;
      if (confirmed !== glareRef.current) {
        glareRef.current = confirmed;
        setIsGlare(confirmed);
      }
    };

    const timer = setInterval(check, GLARE_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      // A later scan starts with no glare carried over.
      streakRef.current = 0;
      glareRef.current = false;
      setIsGlare(false);
    };
  }, [active, videoEl, stageEl, guideEl]);

  const isGlareNow = useCallback(() => glareRef.current, []);

  return { isGlare, isGlareNow };
};
